import type { IFilterPanelBaseFilterModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";

import createCrmScopeFilter from "../models/CrmScopeFilter/factory";
import { visitsFromDefinition } from "../models/VisitsFromFilter/definition";
import type { LeadsFilterBuildContext } from "../types/leadsFilterBuildContext";
import type { LeadsSearchAllFiltersTypes } from "../types/leadsSearchFilters.dto";

type PanelProductDefaultFilterKey = Extract<keyof LeadsSearchAllFiltersTypes, "countries" | "crmScope">;

const isPanelProductDefaultFilterKey = (key: string): key is PanelProductDefaultFilterKey =>
    key === "countries" || key === "crmScope";

const getVisitsFromFilterModel = (): IFilterPanelBaseFilterModel<LeadsSearchAllFiltersTypes["countries"]> => {
    const built = visitsFromDefinition.build({} as LeadsFilterBuildContext);

    if (!built) {
        throw new Error("Visits From filter model is required for panel default checks");
    }

    // `SearchFilterInstance` types `filter` as a union over all DTO keys; this definition is always Visits From.
    return built.filter as IFilterPanelBaseFilterModel<LeadsSearchAllFiltersTypes["countries"]>;
};

const getCrmScopeFilterModel = (): IFilterPanelBaseFilterModel<LeadsSearchAllFiltersTypes["crmScope"]> =>
    createCrmScopeFilter().filter as IFilterPanelBaseFilterModel<LeadsSearchAllFiltersTypes["crmScope"]>;

const getPanelProductDefaultFilterModel = (
    key: PanelProductDefaultFilterKey,
): IFilterPanelBaseFilterModel<LeadsSearchAllFiltersTypes[PanelProductDefaultFilterKey]> => {
    if (key === "countries") {
        return getVisitsFromFilterModel();
    }

    return getCrmScopeFilterModel();
};

/** Visits From initial DTO when the panel value is unset (matches `VisitsFromFilterModel.fromApiValue`). */
export const getVisitsFromPanelInitialCountries = (): LeadsSearchAllFiltersTypes["countries"] =>
    getVisitsFromFilterModel().getInitialDtoValue() ?? [];

/**
 * Whether a flat panel value is still the filter model's product default (not a user-applied selection).
 * Delegates to each filter's `isFilterDirty` — the same signal `createFiltersManager` uses.
 */
export const isLeadsPanelFilterAtProductDefault = (key: string, value: unknown): boolean => {
    if (!isPanelProductDefaultFilterKey(key)) {
        return false;
    }

    const model = getPanelProductDefaultFilterModel(key);

    return !model.isFilterDirty(value as LeadsSearchAllFiltersTypes[PanelProductDefaultFilterKey]);
};
